import React, { useEffect, useState } from "react";

import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { InfoPanel, ToolbarTop } from "../../../components";
import { ContentArea } from "../../../components/ContentArea";
import { useAuth } from "../../../hooks";
import { SchoolCandidateRemarkType } from "../../../models/SchoolCandidate/RemarkTypes";
import {
  useDeleteRemarkMutation,
  useGetRemarksQuery,
  useSaveRemarkMutation,
  useUpdateRemarkMutation,
} from "../../../queries/remarkQuery";
import { NoteContainer } from "./components";
import {
  isAllowedToCreateNote,
  canEditNote,
  canDeleteNote,
  getNoteSource,
} from "./components/utils";

export const SchoolNotePage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const schoolCandidateId = parseInt(id || "0");
  const [error, setError] = useState<string | null>(null);

  const [isAdding, setIsAdding] = useState(false);

  const {
    data: remarks = [],
    isLoading,
    refetch: refetchRemarks,
  } = useGetRemarksQuery(schoolCandidateId);
  const [saveRemark] = useSaveRemarkMutation();
  const [updateRemark] = useUpdateRemarkMutation();
  const [deleteRemark] = useDeleteRemarkMutation();

  useEffect(() => {
    // Used for when the user comes to this page
    // but there has been potential changes from different users
    refetchRemarks();
  }, [refetchRemarks]);

  const handleAddNote = () => {
    setIsAdding(true);
  };

  const successApi = () => {
    refetchRemarks();
    setError(null);
  };

  const handleSaveNote = async (remark: string) => {
    try {
      await saveRemark({
        schoolCandidateId,
        schoolCandidateRemarkTypeId: SchoolCandidateRemarkType.GeneralRemark,
        remark,
      });
      setIsAdding(false);
      successApi();
    } catch (error) {
      setError(t("schoolAttendance.saveFailed"));
      console.error("Failed to save note:", error);
    }
  };

  const handleUpdateNote = async (remarkId: number, remark: string) => {
    try {
      await updateRemark({
        id: remarkId,
        schoolCandidateId,
        schoolCandidateRemarkTypeId: SchoolCandidateRemarkType.GeneralRemark,
        remark,
      });
      successApi();
    } catch (error) {
      setError(t("schoolAttendance.updateFailed"));
      console.error("Failed to update note:", error);
    }
  };

  const handleDeleteNote = async (remarkId: number) => {
    try {
      await deleteRemark(remarkId);
      successApi();
    } catch (error) {
      setError(t("schoolAttendance.deleteFailed"));
      console.error("Failed to delete note:", error);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const canCreate = isAllowedToCreateNote(auth);

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("schoolAttendance.title")}
        showCustom
        showBack="show"
        showAdd={canCreate ? "show" : "disable"}
        onClickAdd={handleAddNote}
      />
      {!!error && (
        <InfoPanel color="danger" fullWidth={true}>
          {error}
        </InfoPanel>
      )}

      <Row>
        <Col>
          {isAdding && (
            <NoteContainer
              isAdding={isAdding}
              onSave={handleSaveNote}
              showButtons={{
                save: true,
              }}
              onCancel={handleCancel}
            />
          )}
          {!remarks.length && !isAdding ? (
            <div>{t("schoolAttendance.noNotes")}</div>
          ) : (
            remarks.map((remark) => (
              <NoteContainer
                key={remark.id}
                title={getNoteSource(t, remark.role)}
                date={hrDate(
                  new Date(remark.dateUpdated || remark.dateCreated)
                )}
                note={remark.remark}
                showButtons={{
                  edit: canEditNote(auth, remark.appUserId, remark.role),
                  delete: canDeleteNote(auth, remark.appUserId, remark.role),
                }}
                onSave={(noteRemark) => handleUpdateNote(remark.id, noteRemark)}
                onDelete={() => handleDeleteNote(remark.id)}
                onCancel={handleCancel}
              />
            ))
          )}
        </Col>
      </Row>
    </ContentArea>
  );
};
